/**
 * OEM+ frozen clock — the single "now" for the whole OEM+ port.
 *
 * ## The rule
 *
 * **Nothing in the OEM port may call `Date.now()` or a zero-argument `new
 * Date()`.** Every "today", "days remaining", "overdue by" and "last heard"
 * figure resolves through this module.
 *
 * ## Why
 *
 * The OEM+ dataset under `src/versions/v1/data/` and `public/data/oem/` is a
 * **frozen snapshot** extracted from Jay's `OEM+_Platform_Demo.html`. Its
 * contents were generated against a fixed instant: **09 September 2026, 06:00
 * AEST**. Contract end dates, PM due dates, work-order timestamps, RMS "last
 * heard" stamps and the 12-day telemetry windows are all positioned relative to
 * that instant. A live clock would drift every derived figure away from the
 * data — renewals would silently expire, `pmDue(30)` would empty out, and the
 * telemetry charts would run off the end of their series — so the demo would
 * decay a little more each day it is shown.
 *
 * ## Two frozen clocks, both deliberate
 *
 * This repo now carries **two** unrelated frozen instants. They must both stay
 * frozen and must not be conflated:
 *
 * | Clock | Instant | Owner | Purpose |
 * |---|---|---|---|
 * | `OEM_NOW_MS` (this file) | 2026-09-09 06:00 AEST | OEM+ port | The dataset's own "now" |
 * | `SNAPSHOT_INSTANT_MS` | 2026-06-08 11:30 UTC | foundation | Parity snapshot determinism (`scripts/parity/render-snapshots.mjs:28`) |
 *
 * The parity harness stubs the global `Date` to *its* instant while rendering
 * snapshots. Because the OEM surfaces read their time from this module rather
 * than from the global clock, that stubbing does not move OEM figures — which
 * is exactly what makes both snapshots and OEM figures reproducible.
 *
 * (A third, also-frozen anchor exists for the admin seed:
 * `ADMIN_SEED_ANCHOR_MS`, 2026-07-15 UTC. Same principle, different lane.)
 *
 * ## Ported from `OEM+_Platform_Demo.html`
 *
 * - `const TODAY='2026-09-09'` — source line 2822 → {@link OEM_TODAY}
 * - `const daysTo=d=>Math.round((new Date(d)-new Date(TODAY))/864e5)` — source
 *   line 2823 → {@link daysTo}
 *
 * Shared layer (layer 2 support): pure, no React, no side effects, no state.
 *
 * @see scripts/oem/README.md — what the snapshot contains
 * @see docs/foundations/4-layer-architecture.md
 */

// ---------------------------------------------------------------------------
// The instant
// ---------------------------------------------------------------------------

/**
 * The dataset's "now" as an ISO-8601 string with its true offset: **09 Sep
 * 2026, 06:00 AEST** (UTC+10, the start of day shift at the eastern sites).
 *
 * The offset is written out rather than left implicit so the constant means the
 * same instant on any machine, whatever `TZ` is set to.
 */
export const OEM_NOW_ISO = '2026-09-09T06:00:00+10:00' as const;

/**
 * {@link OEM_NOW_ISO} as epoch milliseconds: **1788897600000**
 * (= `2026-09-08T20:00:00.000Z`).
 *
 * Derived once at module load with `Date.parse`, which is fully specified for
 * an offset-bearing ISO string — the value does not depend on the host's
 * timezone, locale or wall clock.
 */
export const OEM_NOW_MS: number = Date.parse(OEM_NOW_ISO);

/**
 * A **fresh** `Date` positioned at {@link OEM_NOW_MS}, one new instance per
 * call.
 *
 * `Date` is mutable (`setHours`, `setDate`, …). Handing out a shared module-level
 * instance would let one caller's `.setDate(…)` silently move "now" for every
 * other surface in the app — a bug that only shows up on whichever screen the
 * user visits second. Always allocate.
 *
 * @returns a new `Date` at the frozen instant.
 */
export function oemNow(): Date {
  return new Date(OEM_NOW_MS);
}

// ---------------------------------------------------------------------------
// The date string — the demo's TODAY
// ---------------------------------------------------------------------------

/**
 * The demo's `TODAY` (source line 2822): the frozen instant's **AEST calendar
 * date** in `YYYY-MM-DD` form.
 *
 * This is the form every date-string comparison in the ported engine uses,
 * because the extracted data stores dates as `YYYY-MM-DD` strings and the demo
 * compares them lexically — e.g. `w.created > TODAY` at source line 2833.
 *
 * ### Not the UTC date of {@link OEM_NOW_MS}
 *
 * 06:00 AEST is 20:00 UTC on the *previous* day, so
 * `oemNow().toISOString().slice(0, 10)` yields `'2026-09-08'`, not this value.
 * That is correct and intended: `OEM_TODAY` is the date the sites were working,
 * which is what the data is keyed by. Never derive one of these two constants
 * from the other — use `OEM_TODAY` for date strings and `OEM_NOW_MS` for
 * instants.
 */
export const OEM_TODAY = '2026-09-09' as const;

/**
 * {@link OEM_TODAY} parsed as epoch milliseconds — UTC midnight on that date,
 * per the ECMAScript date-only-form rule. This is the exact value the demo's
 * `new Date(TODAY)` produces, and it is the baseline {@link daysTo} measures
 * against.
 *
 * @internal Exported for tests; prefer {@link daysTo} / {@link OEM_TODAY}.
 */
export const OEM_TODAY_MS: number = Date.parse(OEM_TODAY);

// ---------------------------------------------------------------------------
// Day arithmetic
// ---------------------------------------------------------------------------

/** Milliseconds in a day — the demo's `864e5`. */
const MS_PER_DAY = 864e5;

/**
 * Whole days from {@link OEM_TODAY} to `dateStr`. Literal port of the demo's
 * `daysTo` (source line 2823).
 *
 * **Signed**: positive is in the future, `0` is today, **negative means
 * overdue**. The demo's `daysWord` (source line 3394) renders the sign as
 * `"{n} d"` / `"today"` / `"{n} d overdue"`.
 *
 * Both sides are parsed with the date-only form, which ECMAScript fixes at UTC
 * midnight, so the subtraction is an exact whole number of days and the
 * `Math.round` is a no-op — it is kept because the demo keeps it, and because
 * it makes the function safe for the rare caller that passes a full timestamp.
 *
 * Callers that hold a timestamp (`'2026-09-09T01:00'`) should slice it first,
 * as the demo does at source line 2833: `daysTo(w.created.slice(0, 10))`.
 *
 * `NaN` in, `NaN` out — an unparseable string yields `NaN` exactly as the demo
 * does. This is deliberate: it surfaces bad data loudly rather than rendering
 * it as "today".
 *
 * @param dateStr a `YYYY-MM-DD` date string (or any `Date.parse`-able string).
 * @returns signed whole days; negative when `dateStr` is in the past.
 *
 * @example
 * daysTo('2027-06-30'); // 294 — contract renewal, well clear
 * daysTo('2026-09-09'); // 0   — due today
 * daysTo('2026-09-02'); // -7  — seven days overdue
 */
export function daysTo(dateStr: string): number {
  return Math.round((Date.parse(dateStr) - OEM_TODAY_MS) / MS_PER_DAY);
}

/*
 * NOTE — `daysSince()` is deliberately absent.
 *
 * The brief allowed a `daysSince(dateStr)` inverse "only if you can point at a
 * demo usage". There is none: `OEM+_Platform_Demo.html` contains zero
 * occurrences of `daysSince`, and no `new Date(TODAY) - new Date(x)`
 * (reversed-operand) expression. The two `-daysTo(` hits in the source (lines
 * 3443, 3534) are both sort comparators of the form
 * `daysTo(a.nextdue_date) - daysTo(b.nextdue_date)`, not an age calculation.
 *
 * Age-since is therefore spelled `-daysTo(d)` at the call site, which keeps one
 * definition of the day arithmetic. Add `daysSince` here only when a ported
 * surface actually needs it.
 */
